/** @jsxImportSource @emotion/react */
"use client";

import { useTranslations } from "@i18n/react";
import { forwardRef } from "react";

import type { ThemeType } from "../../../../../global/emotion";
import { spacing } from "../../../../../global/emotion";
import { Text } from "../../../_base/_core/Text/Text.emotion";
import { useFormField } from "./FormField.emotion";

export type FormMessageProps = React.HTMLAttributes<HTMLParagraphElement> & {
  showErrorMessage?: boolean;
  showWarningMessage?: boolean;
};

export const FormMessage = forwardRef<HTMLParagraphElement, FormMessageProps>(
  ({ children, showErrorMessage, showWarningMessage, ...props }, ref) => {
    const { error, warning, formMessageId } = useFormField();
    const t = useTranslations();

    let body = children;
    const errorOrWarningMessage =
      showErrorMessage && error
        ? String(error?.message)
        : showWarningMessage && warning
          ? String(warning)
          : null;

    if (errorOrWarningMessage) {
      try {
        const translated = t(errorOrWarningMessage);
        body =
          translated !== errorOrWarningMessage
            ? translated
            : errorOrWarningMessage;
      } catch {
        // If translation fails, use the original message
        body = errorOrWarningMessage;
      }
    }

    if (!body) {
      return null;
    }

    return (
      <Text
        ref={ref}
        type="detail"
        size="l"
        id={formMessageId}
        css={(theme: ThemeType) => ({
          color: error ? theme.semanticColors.errorError : theme.colors.yellow,
          marginHorizontal: spacing.xs,
        })}
        {...props}
      >
        {body}
      </Text>
    );
  },
);

FormMessage.displayName = "FormMessage";
